import React, { useState, useEffect } from 'react';
import { Package, Boxes, Search, Plus, X } from 'lucide-react';
import axios from 'axios';
import QuickAddCompanyModal from '../components/QuickAddCompanyModal';
import { useNavigate } from 'react-router-dom';

const MaterialsList = () => {
    const [materials, setMaterials] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [vendors, setVendors] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [showQuickAddCompany, setShowQuickAddCompany] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        categoryID: '',
        vendorID: '',
        name: '',
        description: '',
        sku: '',
        unitOfMeasure: '',
        minimumStock: '',
        currentStock: '',
        reorderPoint: '',
        unitCost: '',
        notes: '',
        materialType: '',
        casePack: '',
        bottleVolume: '',
        bottleType: '',
        bottleStyle: '',
        bottleColor: '',
        printStyle: '',
        materialColor: ''
    });

    const fetchMaterials = async () => {
        try {
            const materialsResponse = await axios.get('/api/materials', {
                params: { 
                    page,
                    search,
                    categoryID: selectedCategory || undefined
                }
            });
            setMaterials(materialsResponse.data.materials);
            setTotalPages(materialsResponse.data.totalPages);
        } catch (error) {
            console.error('Error fetching materials:', error);
        }
    };

    const handleEdit = (material) => {
        // Include the new fields when setting form data
        setFormData({
            categoryID: material.categoryID,
            vendorID: material.vendorID,
            name: material.name,
            description: material.description || '',
            sku: material.sku || '',
            unitOfMeasure: material.unitOfMeasure || '',
            minimumStock: material.minimumStock || '',
            currentStock: material.currentStock || '',
            reorderPoint: material.reorderPoint || '',
            unitCost: material.unitCost || '',
            notes: material.notes || '',
            // New fields
            materialType: material.materialType || '',
            casePack: material.casePack || '',
            bottleVolume: material.bottleVolume || '',
            bottleType: material.bottleType || '',
            bottleStyle: material.bottleStyle || '',
            bottleColor: material.bottleColor || '',
            printStyle: material.printStyle || '',
            materialColor: material.materialColor || ''
        });
        setEditingId(material.materialID);
        setShowForm(true);
        window.scrollTo(0, 0);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [categoriesResponse, vendorsResponse] = await Promise.all([
                    axios.get('/api/material-categories'),
                    axios.get('/api/companies', { params: { type: 'vendor' } })
                ]);
        
                setCategories(categoriesResponse.data);
                setVendors(vendorsResponse.data);
                await fetchMaterials();
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [page, search, selectedCategory]);

    const handleAddNew = () => {
        setFormData({
            categoryID: '',
            vendorID: '',
            name: '',
            description: '',
            sku: '',
            unitOfMeasure: '',
            minimumStock: '',
            currentStock: '',
            reorderPoint: '',
            unitCost: '',
            notes: ''
        });
        setEditingId(null);
        setShowForm(true);
        window.scrollTo(0, 0);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            // Clone the form data to avoid modifying the state directly
            const saveData = { ...formData };
            
            if (editingId) {
                await axios.put(`/api/materials/${editingId}`, saveData);
            } else {
                await axios.post('/api/materials', saveData);
            }
            setShowForm(false);
            setEditingId(null);
            fetchMaterials();
        } catch (error) {
            console.error('Error saving material:', error);
        }
    };

    if (loading) {
        return (
            <div className="container mt-4">
                <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Toolbar */}
            <div className="sticky-top bg-light border-bottom" style={{ top: '0', zIndex: 1020 }}>
                <div className="container-fluid py-2">
                    <div className="d-flex justify-content-between align-items-center">
                        {/* Left side group */}
                        <div className="d-flex align-items-center gap-3">
                            <Boxes size={24} className="flex-shrink-0" />
                            <h5 className="mb-0">Materials</h5>
                            
                            <div className="input-group" style={{ width: 'auto' }}>
                                <span className="input-group-text">
                                    <Search size={18} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search materials..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            
                            <select
                                className="form-select"
                                style={{ width: '200px' }}
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <option value="">All Categories</option>
                                {categories.map((category) => (
                                    <option key={category.categoryID} value={category.categoryID}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Right side group */}
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-outline-secondary d-flex align-items-center"
                                onClick={() => navigate('/material-inventory')}
                            >
                                <Package size={18} className="me-2" />
                                Material Inventory
                            </button>
                            <button 
                                className="btn btn-outline-primary d-flex align-items-center"
                                onClick={handleAddNew}
                                title="Add a new item"
                            >
                                <Plus size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mt-4">
                {/* Inline Form */}
                {showForm && (
                    <div className="card mb-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">{editingId ? 'Edit Material' : 'Add New Material'}</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => {
                                    setShowForm(false);
                                    setEditingId(null);
                                }}
                            ></button>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSave}>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Category</label>
                                        <select
                                            className="form-select"
                                            value={formData.categoryID}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                categoryID: e.target.value
                                            })}
                                            required
                                        >
                                            <option value="">Select Category</option>
                                            {categories.map((category) => (
                                                <option 
                                                    key={category.categoryID} 
                                                    value={category.categoryID}
                                                >
                                                    {category.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Vendor</label>
                                        <div className="input-group">
                                            <select
                                                className="form-select"
                                                value={formData.vendorID}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    vendorID: e.target.value
                                                })}
                                            >
                                                <option value="">Select Vendor</option>
                                                {vendors.map((vendor) => (
                                                    <option 
                                                        key={vendor.id} 
                                                        value={vendor.id}
                                                    >
                                                        {vendor.companyName}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => setShowQuickAddCompany(true)}
                                            >
                                                <Plus size={18} />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="col-md-8 mb-3">
                                        <label className="form-label">Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.name}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                name: e.target.value
                                            })}
                                            required
                                        />
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">SKU</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.sku}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                sku: e.target.value
                                            })}
                                        />
                                    </div>

                                    <div className="col-12 mb-3">
                                        <label className="form-label">Description</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={formData.description}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                description: e.target.value
                                            })}
                                        ></textarea>
                                    </div>

                                    <div className="col-md-3 mb-3">
                                        <label className="form-label">Unit of Measure</label>
                                        <select
                                            className="form-select"
                                            value={formData.unitOfMeasure}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                unitOfMeasure: e.target.value
                                            })}
                                            required
                                        >
                                            <option value="">Select Unit</option>
                                            <option value="Each">Each</option>
                                            <option value="Case">Case</option>
                                            <option value="Box">Box</option>
                                            <option value="Kg">Kg</option>
                                            <option value="g">g</option>
                                            <option value="L">L</option>
                                            <option value="mL">mL</option>
                                        </select>
                                    </div>

                                    <div className="col-md-3 mb-3">
                                        <label className="form-label">Current Stock</label>
                                        <input
                                            type="number"
                                            step="1"
                                            className="form-control"
                                            value={formData.currentStock}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                currentStock: e.target.value
                                            })}
                                            required
                                        />
                                    </div>

                                    <div className="col-md-3 mb-3">
                                        <label className="form-label">Minimum Stock</label>
                                        <input
                                            type="number"
                                            step="1"
                                            className="form-control"
                                            value={formData.minimumStock}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                minimumStock: e.target.value
                                            })}
                                        />
                                    </div>

                                    <div className="col-md-3 mb-3">
                                        <label className="form-label">Reorder Point</label>
                                        <input
                                            type="number"
                                            step="1"
                                            className="form-control"
                                            value={formData.reorderPoint}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                reorderPoint: e.target.value
                                            })}
                                        />
                                    </div>

                                    <div className="col-md-3 mb-3">
                                        <label className="form-label">Unit Cost</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            value={formData.unitCost}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                unitCost: e.target.value
                                            })}
                                        />
                                    </div>
                                </div>
                                {/* New Fields Section - Add before the final buttons */}
                                    <h6 className="mt-4 mb-3">Material Specific Properties</h6>
                                    <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Material Type</label>
                                        <select
                                        className="form-select"
                                        value={formData.materialType || ''}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            materialType: e.target.value
                                        })}
                                        >
                                        <option value="">Select Type</option>
                                        <option value="Bottle">Bottle</option>
                                        <option value="Cork">Cork</option>
                                        <option value="Capsule">Capsule</option>
                                        <option value="Label">Label</option>
                                        <option value="Carton">Carton</option>
                                        <option value="Other">Other</option>
                                        </select>
                                    </div>
                                    
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Case Pack (units/case)</label>
                                        <input
                                        type="number"
                                        className="form-control"
                                        value={formData.casePack || ''}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            casePack: e.target.value
                                        })}
                                        placeholder="e.g., 12, 24, 1000"
                                        />
                                    </div>
                                    </div>

                                    {/* Bottle-specific fields - conditionally rendered */}
                                    {formData.materialType === 'Bottle' && (
                                    <>
                                        <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Bottle Volume (ml)</label>
                                            <input
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            value={formData.bottleVolume || ''}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                bottleVolume: e.target.value
                                            })}
                                            placeholder="e.g., 750, 375, 1500"
                                            />
                                        </div>
                                        
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Bottle Type</label>
                                            <select
                                            className="form-select"
                                            value={formData.bottleType || ''}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                bottleType: e.target.value
                                            })}
                                            >
                                            <option value="">Select Type</option>
                                            <option value="Burgundy">Burgundy</option>
                                            <option value="Bordeaux">Bordeaux</option>
                                            <option value="Flint">Flint</option>
                                            <option value="Sparkling">Sparkling</option>
                                            <option value="Other">Other</option>
                                            </select>
                                        </div>
                                        
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Bottle Style</label>
                                            <input
                                            type="text"
                                            className="form-control"
                                            value={formData.bottleStyle || ''}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                bottleStyle: e.target.value
                                            })}
                                            />
                                        </div>
                                        </div>
                                        
                                        <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Bottle Color</label>
                                            <select
                                            className="form-select"
                                            value={formData.bottleColor || ''}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                bottleColor: e.target.value
                                            })}
                                            >
                                            <option value="">Select Color</option>
                                            <option value="AG">Antique Green</option>
                                            <option value="Flint">Flint (Clear)</option>
                                            <option value="Green">Green</option>
                                            <option value="Blue">Blue</option>
                                            <option value="Amber">Amber</option>
                                            </select>
                                        </div>
                                        </div>
                                    </>
                                    )}

                                    {/* Cork and Capsule specific fields */}
                                    {(formData.materialType === 'Cork' || formData.materialType === 'Capsule') && (
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                        <label className="form-label">Print Style</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.printStyle || ''}
                                            onChange={(e) => setFormData({
                                            ...formData,
                                            printStyle: e.target.value
                                            })}
                                            placeholder="e.g., Logo, Text, Branded"
                                        />
                                        </div>
                                        
                                        <div className="col-md-4 mb-3">
                                        <label className="form-label">Material Color</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.materialColor || ''}
                                            onChange={(e) => setFormData({
                                            ...formData,
                                            materialColor: e.target.value
                                            })}
                                            placeholder="e.g., Black, Burgundy, Gold"
                                        />
                                        </div>
                                    </div>
                                    )}
                                <div className="d-flex justify-content-end gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowForm(false);
                                            setEditingId(null);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                    >
                                        {editingId ? 'Update' : 'Save'} Material
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Materials List */}
                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>SKU</th>
                                        <th>Name</th>
                                        <th>Category</th>
                                        <th>Vendor</th>
                                        <th className="text-end">Current Stock</th>
                                        <th className="text-end">Min. Stock</th>
                                        <th className="text-end">Reorder Point</th>
                                        <th className="text-end">Unit Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {materials.map((material) => (
                                        <tr 
                                            key={material.materialID}
                                            onClick={() => handleEdit(material)}
                                            style={{ cursor: 'pointer' }}
                                            className={
                                                material.currentStock <= material.minimumStock 
                                                    ? 'table-danger' 
                                                    : material.currentStock <= material.reorderPoint 
                                                        ? 'table-warning' 
                                                        : ''
                                            }
                                        >
                                            <td>{material.sku}</td>
                                            <td>
                                                {material.name}
                                               {/* {material.description && (
                                                    <>
                                                    <br />
                                                    <small className="text-muted">{material.description}</small>
                                                    </>
                                                    
                                                )}*/}
                                            </td>
                                            <td>{material.categoryName}</td>
                                            <td>{material.vendorName}</td>
                                            <td className="text-end">{material.currentStock} {material.unitOfMeasure}</td>
                                            <td className="text-end">{material.minimumStock} {material.unitOfMeasure}</td>
                                            <td className="text-end">{material.reorderPoint} {material.unitOfMeasure}</td>
                                            <td className="text-end">${material.unitCost}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <nav className="mt-3">
                        <ul className="pagination justify-content-center">
                            {[...Array(totalPages)].map((_, index) => (
                                <li 
                                    key={index}
                                    className={`page-item ${page === index + 1 ? 'active' : ''}`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => setPage(index + 1)}
                                    >
                                        {index + 1}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>
                )}

                {/* Quick Add Company Modal */}
                {showQuickAddCompany && (
                    <QuickAddCompanyModal
                        show={showQuickAddCompany}
                        onClose={() => setShowQuickAddCompany(false)}
                        onSave={async (newVendor) => {
                            try {
                                const response = await axios.post('/api/companies', newVendor);
                                const vendorsResponse = await axios.get('/api/companies', { 
                                    params: { type: 'vendor' } 
                                });
                                setVendors(vendorsResponse.data);
                                setFormData({
                                    ...formData,
                                    vendorID: response.data.id
                                });
                                setShowQuickAddCompany(false);
                            } catch (error) {
                                console.error('Error creating vendor:', error);
                                throw error;
                            }
                        }}
                    />
                )}
            </div>
        </>
    );
};

export default MaterialsList;